import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { HEADER_GAP_PX } from '@/constants/a4';
import { A4_LANDSCAPE } from '@/constants/pageFormat';
import { planSheets, type SheetMetrics, type SheetPlan, type SheetUnit } from './sheetPlan';

export type SheetMode = 'web' | 'print';

export interface SheetLayoutBlock {
  id: string;
  /** Bovenkant van het blok in layoutpixels vanaf de bovenkant van het document. */
  top: number;
  sheetCount: number;
}

export interface SheetLayout {
  pitch: number;
  sheetHeight: number;
  gap: number;
  blocks: SheetLayoutBlock[];
  sheetCount: number;
}

/** Vaste velmaten: gelijk in builder, klantweergave en PDF. */
export function sheetMetricsFor(mode: SheetMode): Omit<SheetMetrics, 'continuationTop'> & { gap: number } {
  const gap = mode === 'web' ? A4_LANDSCAPE.pageGap : 0;
  return {
    gap,
    pitch: A4_LANDSCAPE.height + gap,
    sheetHeight: A4_LANDSCAPE.height,
    bodyBottom: A4_LANDSCAPE.contentHeight - A4_LANDSCAPE.padding,
  };
}

const sheetPaginationKey = new PluginKey<DecorationSet>('sheetPagination');

/** Houdt de verschuivingen bij als decoraties: ProseMirror tekent ze bij elke render opnieuw. */
export const SheetPagination = Extension.create({
  name: 'sheetPagination',
  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: sheetPaginationKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, decorations) {
            const next = tr.getMeta(sheetPaginationKey) as DecorationSet | undefined;
            return next ?? decorations.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations: (state) => sheetPaginationKey.getState(state),
        },
      }),
    ];
  },
});

/**
 * Een verschuiving is lege blokhoogte vóór de inhoud: een `::before` met
 * `--sheet-push`, of tussen tabelrijen en na een regeleinde in een te lange
 * alinea een los spacer-element. Chromium mag
 * zo'n lege hoogte bij het printen over twee vellen verdelen. Padding niet:
 * daarbinnen breekt Chromium nooit, dus dan verhuist het hele element en
 * loopt de PDF uit de pas met de webweergave.
 */
const PUSH_ATTR = 'data-sheet-push';
/** Op de editor tijdens het meten: de verschuivingen van de vorige ronde tellen dan niet mee. */
const MEASURING_ATTR = 'data-sheet-measuring';
/** Elementen buiten ProseMirror (in React-nodeviews) krijgen de verschuiving rechtstreeks. */
const DIRECT_ATTR = 'data-sheet-direct';
const ORIGINAL_PADDING_ATTR = 'data-sheet-original-padding';

const LEAF_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'PRE', 'HR', 'IMG', 'FIGURE', 'CANVAS', 'TR']);
const SHORT_LIST_MAX_ITEMS = 4;
const SHORT_LIST_MAX_HEIGHT = 160;
const SKIP_SELECTOR =
  'style, script, template, colgroup, [data-sheet-skip], [data-sheet-overlay], .ProseMirror-widget';

/** Tabellen in het document zijn grids met `display: contents` op table/tbody. */
function hasBlockChildren(element: Element): boolean {
  return Array.from(element.children).some((child) => {
    const display = getComputedStyle(child).display;
    if (display === 'contents') return hasBlockChildren(child);
    return !display.startsWith('inline') && display !== 'none';
  });
}

function isUnit(element: HTMLElement): boolean {
  if (element.hasAttribute('data-sheet-unit')) return true;
  // Atomaire nodeview (afbeelding, investeringskop/-totaal); een tabel heeft bewerkbare inhoud.
  if (element.classList.contains('react-renderer')) {
    return element.querySelector('[data-node-view-content]') === null;
  }
  if (LEAF_TAGS.has(element.tagName)) return true;
  // Een korte opsomming blijft bij elkaar: geen losse bullets bovenaan een vel.
  if (
    (element.tagName === 'UL' || element.tagName === 'OL') &&
    element.children.length <= SHORT_LIST_MAX_ITEMS &&
    element.offsetHeight <= SHORT_LIST_MAX_HEIGHT
  ) {
    return true;
  }
  if (getComputedStyle(element).display === 'contents') return false;
  return !hasBlockChildren(element);
}

/** Kleinste onderdelen die niet over een velgrens mogen lopen, in leesvolgorde. */
function collectUnitElements(container: HTMLElement): HTMLElement[] {
  const units: HTMLElement[] = [];
  const visit = (element: HTMLElement) => {
    for (const child of Array.from(element.children)) {
      if (!(child instanceof HTMLElement) || child.matches(SKIP_SELECTOR)) continue;
      const style = getComputedStyle(child);
      if (style.display === 'none' || style.position === 'absolute' || style.position === 'fixed') {
        continue;
      }
      if (isUnit(child)) units.push(child);
      else visit(child);
    }
  };
  visit(container);
  return units;
}

function keepsWithNext(element: HTMLElement): boolean {
  return (
    /^H[1-6]$/.test(element.tagName) ||
    element.classList.contains('node-investmentHeaderNode') ||
    element.hasAttribute('data-sheet-keep-with-next') ||
    (element.tagName === 'TR' && element.querySelector(':scope > th') !== null)
  );
}

function shrinkableImage(element: HTMLElement): HTMLImageElement | null {
  if (!element.classList.contains('node-image')) return null;
  const image = element.querySelector('img');
  return image && image.naturalWidth > 0 && image.naturalHeight > 0 ? image : null;
}

/** Positie van de ProseMirror-node waarvan `element` de DOM is; null buiten ProseMirror. */
function nodePosFor(view: EditorView, element: HTMLElement): number | null {
  let pos: number;
  try {
    pos = view.posAtDOM(element, 0);
  } catch {
    return null;
  }
  for (const candidate of [pos - 1, pos, pos - 2]) {
    if (candidate >= 0 && candidate < view.state.doc.content.size && view.nodeDOM(candidate) === element) {
      return candidate;
    }
  }
  return null;
}

function px(value: number): string {
  return `${Math.round(value * 1000) / 1000}px`;
}

function restoreDirectStyles(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>(`[${DIRECT_ATTR}]`).forEach((element) => {
    if (element.hasAttribute(ORIGINAL_PADDING_ATTR)) {
      element.style.paddingTop = element.getAttribute(ORIGINAL_PADDING_ATTR) ?? '';
      element.removeAttribute(ORIGINAL_PADDING_ATTR);
    }
    element.style.removeProperty('--sheet-push');
    element.removeAttribute(PUSH_ATTR);
    element.removeAttribute(DIRECT_ATTR);
  });
}

/** Een `::before` komt alleen boven de inhoud te staan in een blok of kolom-flex. */
function acceptsLeadingBlock(element: HTMLElement): boolean {
  const style = getComputedStyle(element);
  if (style.display === 'flex' || style.display === 'inline-flex') {
    return style.flexDirection.startsWith('column');
  }
  return ['block', 'list-item', 'flow-root'].includes(style.display);
}

function pushDirect(element: HTMLElement, push: number) {
  if (element.tagName !== 'TR') {
    element.setAttribute(DIRECT_ATTR, '');
    element.setAttribute(PUSH_ATTR, '');
    element.style.setProperty('--sheet-push', px(push));
    return;
  }
  // Rij in een React-tabel (oude bloktypes): de cellen schuiven op.
  Array.from(element.children).forEach((cell) => {
    if (!(cell instanceof HTMLElement)) return;
    const original = parseFloat(getComputedStyle(cell).paddingTop) || 0;
    cell.setAttribute(ORIGINAL_PADDING_ATTR, cell.style.paddingTop);
    cell.setAttribute(DIRECT_ATTR, '');
    cell.style.paddingTop = px(original + push);
  });
}

/** Binnen een alinea een span: ook als blok breekt hij de tekst zonder ongeldige HTML. */
function spacer(height: number, inline: boolean): HTMLElement {
  const element = document.createElement(inline ? 'span' : 'div');
  element.className = 'sheet-push-spacer';
  element.setAttribute('aria-hidden', 'true');
  element.contentEditable = 'false';
  element.style.height = px(height);
  return element;
}

type NodeDecorationAttrs = Record<string, string>;

function mergeAttrs(map: Map<number, NodeDecorationAttrs>, pos: number, attrs: NodeDecorationAttrs) {
  const current = map.get(pos) ?? {};
  const style = [current.style, attrs.style].filter(Boolean).join('; ');
  map.set(pos, { ...current, ...attrs, ...(style ? { style } : {}) });
}

interface MeasuredUnit {
  element: HTMLElement;
  unit: SheetUnit;
  image: HTMLImageElement | null;
  /** Tekst na een regeleinde: de verschuiving komt op deze positie in de alinea. */
  linePos?: number;
}

type Measure = (target: Element | Range) => { top: number; height: number };

function textRange(view: EditorView, from: number, to: number): Range {
  const start = view.domAtPos(from);
  const end = view.domAtPos(to);
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  return range;
}

/**
 * Een alinea die zelfs op een leeg vervolgvel niet past, mag toch niet over
 * de velgrens lopen. Bij een regeleinde (Shift+Enter) kan hij breken: de tekst
 * na elk regeleinde wordt een eigen unit, die met een spacer op die plek in de
 * alinea verschuift. Lege regels tellen niet mee. Een vetgedrukte regel direct
 * boven tekst is een tussenkopje en gaat mee naar het volgende vel.
 */
function lineUnits(view: EditorView, element: HTMLElement, measure: Measure): MeasuredUnit[] | null {
  if (element.tagName !== 'P') return null;
  const pos = nodePosFor(view, element);
  const node = pos === null ? null : view.state.doc.nodeAt(pos);
  if (pos === null || !node?.isTextblock) return null;

  const starts = [pos + 1];
  node.forEach((child, offset) => {
    if (child.type.name === 'hardBreak') starts.push(pos + 1 + offset + child.nodeSize);
  });
  if (starts.length === 1) return null;

  const lines = starts.map((from, index) => {
    const to = index + 1 < starts.length ? starts[index + 1] - 1 : pos + node.nodeSize - 1;
    let text = false;
    let bold = true;
    view.state.doc.nodesBetween(from, to, (child) => {
      if (!child.isText) return;
      text = true;
      if (!child.marks.some((mark) => mark.type.name === 'bold')) bold = false;
    });
    return { from, to, empty: from === to, heading: text && bold };
  });

  const elementTop = measure(element).top;
  return lines.flatMap((line, index): MeasuredUnit[] => {
    if (line.empty) return [];
    const { top, height } = measure(textRange(view, line.from, line.to));
    const keepWithNext = line.heading && lines[index + 1]?.empty === false;
    // De eerste regel heeft geen regeleinde ervoor: die verschuift met de hele alinea.
    return index === 0
      ? [{ element, image: null, unit: { top: elementTop, height: top + height - elementTop, keepWithNext } }]
      : [{ element, image: null, linePos: line.from, unit: { top, height, keepWithNext } }];
  });
}

/** Zelfde sleutels betekent zelfde opmaak: dan hoeft ProseMirror niets opnieuw te tekenen. */
function decorationKeys(decorations: readonly Decoration[]): string {
  return decorations
    .map((decoration) => `${decoration.from}-${decoration.to}:${String(decoration.spec.sheet)}`)
    .sort()
    .join('|');
}

/**
 * Verdeelt het hele document opnieuw over vellen. De verschuivingen van de
 * vorige ronde tellen tijdens het meten niet mee (`MEASURING_ATTR`, zie
 * sheetStyles), daarna volgt één nieuwe set. Alleen een gewijzigde set gaat
 * naar ProseMirror; typen zonder nieuwe velgrens kost zo één meting. Alles
 * gebeurt in één taak: de browser tekent tussendoor niets, dus er is geen
 * knippering.
 */
export function paginateDocument(view: EditorView, mode: SheetMode): SheetLayout {
  const { gap, ...base } = sheetMetricsFor(mode);
  const root = view.dom as HTMLElement;

  const decorations = new Map<number, NodeDecorationAttrs>();
  const spacers: Array<{ pos: number; height: number; inline: boolean }> = [];
  const direct: Array<{ element: HTMLElement; push: number }> = [];
  const blocks: Array<{ id: string; pos: number; sheetCount: number }> = [];

  root.setAttribute(MEASURING_ATTR, '');
  restoreDirectStyles(root);

  // Een getransformeerde (geschaalde) weergave meet in schermpixels; reken terug naar
  // layoutpixels. offsetWidth is afgerond, de berekende breedte niet.
  const layoutWidth = parseFloat(getComputedStyle(root).width);
  const rootRect = root.getBoundingClientRect();
  const scale = layoutWidth > 0 && rootRect.width > 0 ? rootRect.width / layoutWidth : 1;

  try {
    view.state.doc.forEach((node, pos) => {
      const dom = view.nodeDOM(pos);
      if (!(dom instanceof HTMLElement) || !dom.classList.contains('react-renderer')) return;
      const content = dom.firstElementChild;
      const blockRect = dom.getBoundingClientRect();
      const measure: Measure = (target) => {
        const rect = target.getBoundingClientRect();
        return { top: (rect.top - blockRect.top) / scale, height: rect.height / scale };
      };

      let plan: SheetPlan;
      if (!(content instanceof HTMLElement) || content.dataset.fullPage === 'true') {
        plan = planSheets([], 0, { ...base, continuationTop: 0 });
      } else {
        // Vervolgvellen: inhoud op dezelfde hoogte als op het eerste vel, onder de kop.
        const body = content.querySelector('[data-sheet-body]');
        const header = content.querySelector('[data-sheet-skip]');
        const continuationTop = Math.max(
          body ? measure(body).top : A4_LANDSCAPE.padding,
          header ? measure(header).top + measure(header).height + HEADER_GAP_PX : 0,
        );
        const sheetBodyHeight = base.bodyBottom - continuationTop;
        const measured: MeasuredUnit[] = collectUnitElements(content)
          .flatMap((element): MeasuredUnit[] => {
            const box = measure(element);
            const lines = box.height > sheetBodyHeight ? lineUnits(view, element, measure) : null;
            if (lines) return lines;
            const image = shrinkableImage(element);
            return [
              {
                element,
                image,
                unit: { ...box, keepWithNext: keepsWithNext(element), shrinkable: image !== null },
              },
            ];
          })
          .filter(({ unit }) => unit.height > 0);
        // Een totaalregel staat nooit los bovenaan een vel: hij neemt zijn laatste rij mee.
        measured.forEach((entry, index) => {
          if (measured[index + 1]?.element.classList.contains('node-investmentTotalNode')) {
            entry.unit.keepWithNext = true;
          }
        });
        plan = planSheets(
          measured.map(({ unit }) => unit),
          measure(content).height,
          { ...base, continuationTop },
        );

        plan.placements.forEach((placement, index) => {
          const { element, image, linePos } = measured[index];
          if (placement.push <= 0 && placement.shrinkTo === undefined) return;
          if (linePos !== undefined) {
            spacers.push({ pos: linePos, height: placement.push, inline: true });
            return;
          }
          const elementPos = nodePosFor(view, element);
          if (elementPos === null) {
            if (placement.push > 0) direct.push({ element, push: placement.push });
            return;
          }
          if (placement.shrinkTo !== undefined && image) {
            const aspect = image.naturalWidth / image.naturalHeight;
            // De unit is de nodeview om de afbeelding heen; die is iets hoger dan de afbeelding.
            const overhead = measured[index].unit.height - measure(image).height;
            const imageHeight = Math.max(1, placement.shrinkTo - overhead);
            mergeAttrs(decorations, elementPos, {
              'data-sheet-shrink': '',
              style: `--sheet-image-max-width: ${px(Math.floor(imageHeight * aspect))}`,
            });
          }
          if (placement.push <= 0) return;
          // De eerste rij verschuift de hele tabel, zodat de rand en kop meegaan.
          const firstRow = element.tagName === 'TR' && !element.previousElementSibling;
          const tableWrapper = firstRow ? (element.closest('.tableWrapper') as HTMLElement | null) : null;
          const tablePos = tableWrapper ? nodePosFor(view, tableWrapper) : null;
          const [target, targetPos] =
            tableWrapper && tablePos !== null ? [tableWrapper, tablePos] : [element, elementPos];
          if (acceptsLeadingBlock(target)) {
            mergeAttrs(decorations, targetPos, {
              [PUSH_ATTR]: '',
              style: `--sheet-push: ${px(placement.push)}`,
            });
          } else {
            // Tabelrijen zijn grids: een spacer ertussen staat in de gewone blokflow.
            spacers.push({ pos: targetPos, height: placement.push, inline: false });
          }
        });
      }

      const sheetCount = plan.sheetCount;
      mergeAttrs(decorations, pos, {
        'data-sheet-count': String(sheetCount),
        style: [
          `min-height: ${px(plan.blockHeight)}`,
          `--sheet-content-min-height: ${px((sheetCount - 1) * base.pitch + A4_LANDSCAPE.contentHeight)}`,
        ].join('; '),
      });
      blocks.push({ id: String(node.attrs.id ?? pos), pos, sheetCount });
    });
  } finally {
    root.removeAttribute(MEASURING_ATTR);
  }

  const next = [
    ...Array.from(decorations.entries()).map(([pos, attrs]) => {
      const node = view.state.doc.nodeAt(pos);
      return Decoration.node(pos, pos + (node?.nodeSize ?? 1), attrs, { sheet: JSON.stringify(attrs) });
    }),
    ...spacers.map(({ pos, height, inline }) =>
      Decoration.widget(pos, () => spacer(height, inline), {
        side: -1,
        key: `sheet-spacer-${pos}-${height.toFixed(3)}`,
        sheet: `spacer ${height.toFixed(3)}`,
        ignoreSelection: true,
      }),
    ),
  ];
  const current = sheetPaginationKey.getState(view.state) ?? DecorationSet.empty;
  if (decorationKeys(current.find()) !== decorationKeys(next)) {
    const decorationSet = DecorationSet.create(view.state.doc, next);
    view.dispatch(view.state.tr.setMeta(sheetPaginationKey, decorationSet).setMeta('addToHistory', false));
  }
  direct.forEach(({ element, push }) => pushDirect(element, push));

  const documentTop = root.getBoundingClientRect().top;
  const layoutBlocks = blocks.map(({ id, pos, sheetCount }) => {
    const dom = view.nodeDOM(pos) as HTMLElement;
    return { id, sheetCount, top: (dom.getBoundingClientRect().top - documentTop) / scale };
  });
  return {
    pitch: base.pitch,
    sheetHeight: base.sheetHeight,
    gap,
    blocks: layoutBlocks,
    sheetCount: layoutBlocks.reduce((sum, block) => sum + block.sheetCount, 0),
  };
}
